import type { ListTaskInstancesRequest } from '@mini-cloud/shared';
import type { MetricSeriesKey, MetricWindowKey } from '@/hooks/use-metrics';

/**
 * Every cache key the console uses, built from one place.
 *
 * Keys are hierarchical so that invalidating `['tasks']` also drops every task
 * detail and dynamics entry — a launch or a delete changes more than the one row it
 * names, and hand-listing the affected keys at each call site is how stale panels
 * appear after a mutation.
 */
export const queryKeys = {
  tasks: () => ['tasks'] as const,
  task: (taskId: string, version?: number) => ['tasks', taskId, version ?? 'latest'] as const,
  taskDynamics: (taskId: string) => ['tasks', taskId, 'dynamics'] as const,

  instances: (filter: ListTaskInstancesRequest = {}) => ['instances', filter] as const,
  instance: (instanceId: string) => ['instances', instanceId] as const,
  instanceEvents: (instanceId: string) => ['instances', instanceId, 'events'] as const,

  metricNamespaces: () => ['metrics', 'namespaces'] as const,
  metricNames: (namespace: string) => ['metrics', 'names', namespace] as const,
  metricDimensions: (namespace: string, metricName: string) => ['metrics', 'dimensions', namespace, metricName] as const,
  /** Every window read for one series, which is where a new window finds data to show while it loads. */
  metricSeries: (series: MetricSeriesKey) => ['metrics', 'data', series] as const,
  metricData: (series: MetricSeriesKey, window: MetricWindowKey) => ['metrics', 'data', series, window] as const,

  dashboards: () => ['dashboards'] as const,
  dashboard: (name: string) => ['dashboards', name] as const,

  monitors: () => ['monitors'] as const,
  monitor: (name: string) => ['monitors', name] as const,
  monitorHistory: (name: string, after?: number) => ['monitors', name, 'history', after ?? 'newest'] as const,
  monitorHistoryBetween: (name: string, from: number, to: number) => ['monitors', name, 'history', 'between', from, to] as const,

  notifiers: () => ['notifiers'] as const,

  agents: () => ['agents'] as const,
  variables: () => ['variables'] as const,
  hubStatus: () => ['hub-status'] as const,
  ping: () => ['ping'] as const,
};
